import { useArchitectureStore } from '../store/useArchitectureStore';
import { MOCK_PALETTES } from '../data/palettes';
import { STYLE_MATRIX } from '../data/styles';
import { useRegionalGuidelines } from './useRegionalGuidelines';
import { ARCHITECTURE_PROMPT_TEMPLATE } from '../config/ArchitecturePromptTemplate';

export const useArchitecturePrompt = () => {
    const { mode, projectType, selectedPaletteId } = useArchitectureStore();
    const { activeGuideline } = useRegionalGuidelines();

    // Helper: Replace tokens in a string with values, and strip lines where the value is empty
    const hydrate = (template: string, values: Record<string, string>) => {
        return template
            // Split into lines to process line-by-line
            .split('\n')
            .map(line => {
                let isEmptyToken = false;
                const hydratedLine = line.replace(/\{\{(\w+)\}\}/g, (_, key) => {
                    const val = values[key];
                    if (!val || val.trim() === '') {
                        isEmptyToken = true;
                        return '';
                    }
                    return val;
                });

                // If the line contained a token that resolved to empty, drop the whole line
                if (isEmptyToken) {
                    return null;
                }
                return hydratedLine;
            })
            .filter(line => line !== null)
            .join('\n');
    };

    const generatePrompt = (baseDescription: string) => {
        const { selectedStyle, selectedStyleMatrixId, timeOfDay } = useArchitectureStore.getState();
        const matrixStyle = selectedStyleMatrixId ? STYLE_MATRIX.find(s => s.id === selectedStyleMatrixId) : null;

        // Only generate elevation prompts
        if (mode !== 'elevation') {
            console.warn('useArchitecturePrompt called but mode is not elevation');
            return '';
        }

        // ------------------------------------------------------------------
        // 1. PREPARE DYNAMIC VALUES
        // ------------------------------------------------------------------
        const values: Record<string, string> = {
            // Context
            voice_instruction: '',
            massing_description: '',
            roof_type: '',
            style_modifier: '',

            // Materials
            base_material: '',
            upper_material: '',
            palette_instruction: '',
            details_instruction: '',

            // Fenestration
            window_ratio: '',
            window_details: '',
            opening_style: '',

            // Atmosphere
            subject_description: '',
            lighting_description: '',
            context_description: '',

            // Elements
            roof_edge: '',
            wall_relief: '',
            base_arches: '',
            feature_accents: ''
        };

        // --- A. Atmosphere & Subject ---
        const detailedSubject = projectType || 'building';

        // Lighting Logic (Render Bridge)
        if (timeOfDay === 'Day') {
            values.lighting_description = "Mid-morning light. The light is crisp, clear, and energetic. Colors are vibrant. Blue sky.";
        } else if (timeOfDay === 'Sunset') {
            values.lighting_description = "Golden Hour. The light is warm, soft, and directional, casting long dramatic shadows and bathing the architecture in a gold-orange hue.";
        } else if (timeOfDay === 'Night') {
            values.lighting_description = "Night time scene. Artificial lights (streetlights, windows) provide the primary illumination. Deep blue ambient skylight, high contrast.";
        } else {
            values.lighting_description = "Natural overcast daylight, soft shadows.";
        }

        values.subject_description = `Architectural photography of a ${detailedSubject} facade.`;

        // User Override
        if (baseDescription) {
            values.context_description = `USER NOTES: ${baseDescription}`;
        }

        // --- B. Regional & Style Data ---
        if (activeGuideline) {
            const data = activeGuideline.guideline_data;
            const currentStyle = data.styles[selectedStyle] || data.styles[data.default_style];
            const regionName = activeGuideline.name;

            // Identity
            values.voice_instruction = data.voice_instruction || '';
            values.massing_description = currentStyle?.massing || '';
            values.roof_type = currentStyle?.roof_type || '';

            // Style Matrix Logic
            if (matrixStyle) {
                values.style_modifier = matrixStyle.prompt_modifier;
                values.details_instruction = `Install ${matrixStyle.name} fixtures and joinery (${matrixStyle.prompt_modifier}).`;

                // Modern Minimalist Fix
                if (matrixStyle.id === 'modern_minimalist') {
                    values.window_details = `Clean, frameless glass installed in existing openings.`;
                } else {
                    values.window_details = `Traditional wooden shutters in existing openings.`;
                }
            } else {
                values.style_modifier = `Authentic ${regionName} architecture.`;
                values.details_instruction = currentStyle?.details?.join(', ') || '';
                values.window_details = `Traditional wooden shutters in existing openings.`;
            }

            // Materials - Extracting from the array [Walls, Base, Wood/Accent] based on migration convention
            // Migration example: ["Stacked Red Sandstone", "Mud/Clay Upper", "Tamarisk Wood"]
            const matArray = currentStyle?.materials || ["Stone", "Plaster", "Wood"];
            values.base_material = matArray[0] || "Regional Stone";
            values.upper_material = matArray[1] || "Regional Plaster";
            values.wood_material = matArray[2] || "Regional Wood";

            values.palette_instruction = data.palette_instruction || '';

            // Dynamic Color Proportions based on DB data for the specific style
            if (currentStyle?.color_proportions) {
                values.primary_proportion = currentStyle.color_proportions.primary;
                values.accent_proportion = currentStyle.color_proportions.accent;
            } else {
                values.primary_proportion = 'Dominant';
                values.accent_proportion = 'Minimal';
            }

            // Fenestration
            values.window_ratio = currentStyle?.window_ratio || '';

            // Elements (Zone Mapping)
            const zoneMap = {
                roof: [] as string[],
                wall: [] as string[],
                opening: [] as string[],
                base: [] as string[],
                general: [] as string[]
            };

            data.elements.forEach((e: any) => {
                const term = e.prompt_terms[0];
                switch (e.type) {
                    case 'parapet': case 'merlon': zoneMap.roof.push(term); break;
                    case 'decoration': case 'frieze': zoneMap.wall.push(term); break;
                    case 'window': case 'ventilation': case 'opening': zoneMap.opening.push(term); break;
                    case 'arch': case 'arcade': zoneMap.base.push(term); break;
                    default: zoneMap.general.push(term);
                }
            });

            if (zoneMap.roof.length > 0) values.roof_edge = `Apply [${zoneMap.roof.join(', ')}] to the skyline silhouette.`;
            if (zoneMap.wall.length > 0) values.wall_relief = `Apply horizontal bands of [${zoneMap.wall.join(', ')}] on the upper facade and above windows.`;
            if (zoneMap.opening.length > 0) values.opening_style = zoneMap.opening.join(', ') + '.';
            if (zoneMap.base.length > 0) values.base_arches = zoneMap.base.join(', ') + '.';
            if (zoneMap.general.length > 0) values.feature_accents = zoneMap.general.join(', ') + '.';
        }

        // ------------------------------------------------------------------
        // 2. COMPILE PROMPT
        // ------------------------------------------------------------------
        const T = ARCHITECTURE_PROMPT_TEMPLATE;
        const parts: string[] = [];

        // 1. Role
        parts.push(T.role);

        // 2. Constraints (Universal)
        parts.push(...T.constraints);

        // 3. Dynamic Sections
        // We iterate through the sections definition and hydrate each
        Object.values(T.sections).forEach(sectionTemplate => {
            const hydratedSection = hydrate(sectionTemplate, values);
            // Only add if it contains meaningful content (not just whitespace/labels)
            // Heuristic: check if it has more than just labels (e.g. contains actual values)
            if (hydratedSection.trim().length > 0) {
                parts.push(hydratedSection.trim());
            }
        });

        // 4. Quality
        parts.push(T.quality);

        return parts.join('\n\n');
    };

    const generateNegativePrompt = () => {
        const { selectedStyle } = useArchitectureStore.getState();

        let negative = ARCHITECTURE_PROMPT_TEMPLATE.negative_universal;
        let dynamicNegatives: string[] = [];

        if (activeGuideline) {
            const data = activeGuideline.guideline_data;

            // 1. Database Negatives
            if (data.forbidden_elements && Array.isArray(data.forbidden_elements)) {
                dynamicNegatives.push(data.forbidden_elements.join(', '));
            }

            // 2. Style Logic Negatives
            if (selectedStyle === 'Traditional') {
                dynamicNegatives.push('modern architecture, glass curtain wall, steel facade, aluminum composite panels, minimal, bauhaus, high-tech, futuristic, smooth white stucco, blue glass, reflective glass, mirror finish, shiny windows, large pane glass');
            } else if (selectedStyle === 'Contemporary') {
                dynamicNegatives.push('rustic stone, mud brick, distressed finish, aged textures, ruin, crumbling');
            } else if (selectedStyle === 'Transitional') {
                dynamicNegatives.push('excessive ornamentation, pure glass box, crumbling ruin, debris, blue glass');
            }
        }

        return negative + (dynamicNegatives.length > 0 ? ', ' + dynamicNegatives.join(', ') : '');
    };

    return { generatePrompt, generateNegativePrompt, activeGuideline };
};